/**
 * Personal calendar event modal — create or edit a private reminder.
 *
 * Resolves to:
 *   { action: 'saved',   item }
 *   { action: 'deleted', id   }
 *   { action: 'cancelled' }
 *
 * Notification channels:
 *   - In-app bell (default ON) — uses existing notifications drawer.
 *   - Browser pop-up (default OFF) — fires `new Notification()` while a Heist
 *     tab is open; on save we ask for permission if the user enabled it.
 *   - Email (default OFF) — sent via Resend.
 */

import { client } from '../api/client.js';

const CATEGORIES = [
    { id: 'write', label: 'Write' },
    { id: 'shoot', label: 'Shoot' },
    { id: 'plan', label: 'Plan' },
    { id: 'research', label: 'Research' },
    { id: 'review', label: 'Review' },
    { id: 'other', label: 'Other' },
];

function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

function isoToLocalInput(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local) {
    if (!local) return null;
    const d = new Date(local);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
}

/**
 * @param {object} opts
 * @param {object|null} [opts.event]    Existing personal_event row to edit (null = create).
 * @param {string}      [opts.defaultDate]  ISO datetime to pre-fill when creating.
 * @param {string}      [opts.brandId]      Brand context for create (server stores it).
 * @returns {Promise<{action: string, item?: object, id?: string}>}
 */
export function openPersonalEventModal({ event = null, defaultDate = null, brandId = null } = {}) {
    return new Promise((resolve) => {
        const isEdit = !!event;
        let settled = false;

        const initial = isEdit ? {
            id: event.id,
            title: event.title || '',
            notes: event.notes || '',
            category: event.category || 'other',
            starts_at: event.starts_at,
            duration_minutes: event.duration_minutes || 30,
            notify_in_app: event.notify_in_app !== false,
            notify_browser_push: !!event.notify_browser_push,
            notify_email: !!event.notify_email,
            status: event.status || 'open',
        } : {
            id: null,
            title: '',
            notes: '',
            category: 'write',
            starts_at: defaultDate || new Date(Date.now() + 60 * 60 * 1000).toISOString(),
            duration_minutes: 30,
            notify_in_app: true,
            notify_browser_push: false,
            notify_email: false,
            status: 'open',
        };

        const overlay = document.createElement('div');
        overlay.className = 'pe-modal-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-labelledby', 'pe-modal-title');

        overlay.innerHTML = `
          <div class="pe-modal" role="document">
            <div class="pe-modal__head">
              <h3 id="pe-modal-title" class="pe-modal__title">${isEdit ? 'Edit reminder' : 'New reminder'}</h3>
              <button type="button" class="pe-modal__close" aria-label="Close" data-action="cancel">&times;</button>
            </div>
            <div class="pe-modal__body">
              <label class="pe-field">
                <span class="pe-field__label">Title</span>
                <input class="pe-input" type="text" data-field="title" placeholder="e.g. Shoot footage for Monday's reel" maxlength="160" value="${escapeHtml(initial.title)}" autofocus />
              </label>
              <label class="pe-field">
                <span class="pe-field__label">Notes <span class="pe-field__hint">optional</span></span>
                <textarea class="pe-input pe-input--area" data-field="notes" rows="3" placeholder="Anything you want to remember when this fires.">${escapeHtml(initial.notes)}</textarea>
              </label>
              <div class="pe-field-row">
                <label class="pe-field pe-field--inline">
                  <span class="pe-field__label">Category</span>
                  <select class="pe-input" data-field="category">
                    ${CATEGORIES.map(c => `<option value="${c.id}" ${initial.category === c.id ? 'selected' : ''}>${c.label}</option>`).join('')}
                  </select>
                </label>
                <label class="pe-field pe-field--inline">
                  <span class="pe-field__label">When</span>
                  <input class="pe-input" type="datetime-local" data-field="starts_at" value="${escapeHtml(isoToLocalInput(initial.starts_at))}" />
                </label>
                <label class="pe-field pe-field--inline pe-field--small">
                  <span class="pe-field__label">Duration</span>
                  <select class="pe-input" data-field="duration_minutes">
                    ${[15, 30, 45, 60, 90, 120, 180, 240, 480].map(m =>
                        `<option value="${m}" ${initial.duration_minutes === m ? 'selected' : ''}>${m < 60 ? m + ' min' : (m / 60) + ' hr'}</option>`).join('')}
                  </select>
                </label>
              </div>
              <fieldset class="pe-channels">
                <legend class="pe-channels__legend">Reminder channels</legend>
                <label class="pe-toggle">
                  <input type="checkbox" data-field="notify_in_app" ${initial.notify_in_app ? 'checked' : ''} />
                  <span class="pe-toggle__label">
                    <strong>Bell notification</strong>
                    <span class="pe-toggle__sub">Lands in your Heist notifications drawer.</span>
                  </span>
                </label>
                <label class="pe-toggle">
                  <input type="checkbox" data-field="notify_browser_push" ${initial.notify_browser_push ? 'checked' : ''} />
                  <span class="pe-toggle__label">
                    <strong>Browser pop-up</strong>
                    <span class="pe-toggle__sub">A desktop notification while a Heist tab is open. Asks for permission the first time.</span>
                  </span>
                </label>
                <label class="pe-toggle">
                  <input type="checkbox" data-field="notify_email" ${initial.notify_email ? 'checked' : ''} />
                  <span class="pe-toggle__label">
                    <strong>Email</strong>
                    <span class="pe-toggle__sub">Sent to the email on your Heist account.</span>
                  </span>
                </label>
              </fieldset>
              <div class="pe-error" data-role="error" hidden></div>
            </div>
            <div class="pe-modal__foot">
              <div class="pe-modal__foot-left">
                ${isEdit ? `
                  <button type="button" class="pe-btn pe-btn--ghost" data-action="mark-done">${initial.status === 'done' ? 'Mark open' : 'Mark done'}</button>
                  <button type="button" class="pe-btn pe-btn--danger" data-action="delete">Delete</button>
                ` : ''}
              </div>
              <div class="pe-modal__foot-right">
                <button type="button" class="pe-btn pe-btn--ghost" data-action="cancel">Cancel</button>
                <button type="button" class="pe-btn pe-btn--primary" data-action="save">${isEdit ? 'Save' : 'Create reminder'}</button>
              </div>
            </div>
          </div>
        `;

        const cleanup = (result) => {
            if (settled) return;
            settled = true;
            document.removeEventListener('keydown', onKey);
            overlay.remove();
            resolve(result);
        };

        const onKey = (e) => {
            if (e.key === 'Escape') cleanup({ action: 'cancelled' });
        };

        const showError = (msg) => {
            const el = overlay.querySelector('[data-role="error"]');
            if (!el) return;
            if (msg) {
                el.textContent = msg;
                el.hidden = false;
            } else {
                el.hidden = true;
                el.textContent = '';
            }
        };

        const readForm = () => {
            const get = (field) => overlay.querySelector(`[data-field="${field}"]`);
            const checked = (field) => !!get(field)?.checked;
            return {
                title: get('title')?.value?.trim() || '',
                notes: get('notes')?.value || '',
                category: get('category')?.value || 'other',
                starts_at: localInputToIso(get('starts_at')?.value || ''),
                duration_minutes: parseInt(get('duration_minutes')?.value || '30', 10),
                notify_in_app: checked('notify_in_app'),
                notify_browser_push: checked('notify_browser_push'),
                notify_email: checked('notify_email'),
            };
        };

        const setBusy = (busy) => {
            overlay.querySelectorAll('button').forEach(b => { b.disabled = busy; });
        };

        const requestPushPermissionIfWanted = async (form) => {
            if (!form.notify_browser_push) return;
            if (typeof Notification === 'undefined') return;
            if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
            try { await Notification.requestPermission(); } catch (_) {}
        };

        overlay.addEventListener('click', async (e) => {
            const target = e.target.closest('[data-action]');
            if (!target) {
                if (e.target === overlay) cleanup({ action: 'cancelled' });
                return;
            }
            const action = target.dataset.action;

            if (action === 'cancel') {
                cleanup({ action: 'cancelled' });
                return;
            }

            if (action === 'save') {
                showError(null);
                const form = readForm();
                if (!form.title) { showError('Give the reminder a title.'); return; }
                if (!form.starts_at) { showError('Pick a date and time.'); return; }

                await requestPushPermissionIfWanted(form);

                setBusy(true);
                try {
                    const payload = {
                        ...form,
                        ...(brandId ? { brand_id: brandId } : {}),
                    };
                    const res = isEdit
                        ? await client.updatePersonalEvent(initial.id, payload)
                        : await client.createPersonalEvent(payload);
                    if (!res?.ok) throw new Error(res?.error || 'Save failed');
                    cleanup({ action: 'saved', item: res.item });
                } catch (err) {
                    setBusy(false);
                    showError(err.message || 'Could not save reminder.');
                }
                return;
            }

            if (action === 'delete') {
                if (!isEdit) return;
                if (!confirm('Delete this reminder?')) return;
                setBusy(true);
                try {
                    const res = await client.deletePersonalEvent(initial.id);
                    if (!res?.ok) throw new Error(res?.error || 'Delete failed');
                    cleanup({ action: 'deleted', id: initial.id });
                } catch (err) {
                    setBusy(false);
                    showError(err.message || 'Could not delete reminder.');
                }
                return;
            }

            if (action === 'mark-done') {
                if (!isEdit) return;
                setBusy(true);
                try {
                    const newStatus = initial.status === 'done' ? 'open' : 'done';
                    const res = await client.updatePersonalEvent(initial.id, { status: newStatus });
                    if (!res?.ok) throw new Error(res?.error || 'Update failed');
                    cleanup({ action: 'saved', item: res.item });
                } catch (err) {
                    setBusy(false);
                    showError(err.message || 'Could not update reminder.');
                }
                return;
            }
        });

        document.addEventListener('keydown', onKey);
        document.body.appendChild(overlay);

        // Autofocus the first text field
        requestAnimationFrame(() => overlay.querySelector('[data-field="title"]')?.focus());
    });
}
